import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { FakeLink } from '@hivebrite/design-system/Link';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';

import ClampedLinkWorkaround from 'hb-react/shared/components/DesignSystem/ClampedLinkWorkaround';
import useOutboxedFontTheme from 'hb-react/shared/hooks/theme/useOutboxedFontTheme';
import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import { isDisplayedAsLane } from '../constants/displayAs';
import { CATEGORY, MY_TOPICS, SUGGESTED_TOPICS } from '../constants/types';
import { useTopicsIndexContext } from '../contexts/TopicsIndexContext';
import useTopicsMenuName from '../hooks/useTopicsMenuName';

const TopicsBreadcrumb = ({
  displayAs,
  isTopicLoadingCards,
  name,
  onSeeAll,
  totalTopics,
}) => {
  const outboxedFontTheme = useOutboxedFontTheme();
  const {
    areUsingFilters,
    currentView,
    selectedSuperCategory,
    setCategoriesView,
    setSuperCategoryView,
  } = useTopicsIndexContext();

  const topicsMenuName = useTopicsMenuName();

  const foundInfo = areUsingFilters ? __('found') : '';
  const totalTopicsInfo = isTopicLoadingCards ? '' : `(${totalTopics})`;

  const canDisplayParentsLink =
    currentView === CATEGORY ||
    currentView === MY_TOPICS ||
    currentView === SUGGESTED_TOPICS;

  const seeSuperCategory = () => {
    setSuperCategoryView(selectedSuperCategory.id);
  };

  return (
    <ThemeProvider
      theme={outboxedFontTheme.themeName}
      fontBrightness={outboxedFontTheme.fontBrightness}
    >
      <BoxWrapper
        canDisplayParentsLink={canDisplayParentsLink}
        data-testid="topics-breadcrumb"
      >
        <ClampedLinkWorkaround clamp={2} tagName="h2">
          {canDisplayParentsLink && (
            <>
              <FakeLink
                font="h2-semibold"
                as="span"
                display="inline"
                onClick={setCategoriesView}
                onKeyPress={onEnterOrSpacePress(setCategoriesView)}
              >
                {topicsMenuName}
              </FakeLink>
              {` > `}
              {selectedSuperCategory && (
                <>
                  <FakeLink
                    font="h2-semibold"
                    as="span"
                    display="inline"
                    onClick={seeSuperCategory}
                    onKeyPress={onEnterOrSpacePress(seeSuperCategory)}
                  >
                    {selectedSuperCategory.name}
                  </FakeLink>
                  {` > `}
                </>
              )}
            </>
          )}
          {`${name} ${foundInfo} ${totalTopicsInfo}`}
          {isDisplayedAsLane(displayAs) && <span>&nbsp;·&nbsp;</span>}
        </ClampedLinkWorkaround>

        {isDisplayedAsLane(displayAs) && (
          <FakeLink
            font="h2-semibold"
            as="span"
            display="inline"
            onClick={onSeeAll}
            onKeyPress={onEnterOrSpacePress(onSeeAll)}
          >
            {s__('Topics|See all')}
            <ScreenReadersContent>{` ${name}`}</ScreenReadersContent>
          </FakeLink>
        )}
      </BoxWrapper>
    </ThemeProvider>
  );
};

export default TopicsBreadcrumb;

TopicsBreadcrumb.propTypes = {
  displayAs: PropTypes.string.isRequired,
  isTopicLoadingCards: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onSeeAll: PropTypes.func,
  totalTopics: PropTypes.number.isRequired,
};

const getLineHeight = (theme) => `line-height: ${theme.lineHeights[9]}`;

const BoxWrapper = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) => !['canDisplayParentsLink'].includes(prop),
  })
  .attrs({
    alignItems: 'flex-end',
    flexWrap: {
      _: 'wrap',
      m: 'nowrap',
    },
    font: 'h2-semibold',
    justifyContent: 'flex-start',
  })`
  ${({ canDisplayParentsLink, theme }) =>
    canDisplayParentsLink && getLineHeight(theme)}
`;
